import type { AxiosInstance } from 'axios';
import { type ZodError, z } from 'zod';
import type { ApiConfig, ErrorRes } from '../shared/models';
import { getViewerForwardHeaders } from '../shared/viewer-headers';
import type { EXTERNAL_API_V1 } from './external';
import type {
  AUTH_API_V1,
  CMS_API_V1,
  HOSPITAL_API_V1,
  HOSPITAL_PATIENT_NOTICES_API_V1,
  HOSPITAL_RECEPTION_SYMPTOMS_API_V1,
  HOSPITAL_REVIEW_API_V1,
  MEMBERSHIPS_API_V1,
  NAME_CHECK_API_V1,
  RECEPTION_PENALTIES_API_V1,
  RESERVATIONS_API_V1,
  SEARCH_API_V1,
  SHORT_URL_API_V1,
  SMS_CERTIFICATION_API_V1,
  TREATMENTS_API_V1,
  USER_AGREEMENT_API_V1,
  USERS_API_V1,
  WAITINGS_API_V1,
} from './v1';

const DEVELOPMENT_API_URL = 'https://web.test.api.ddocdoc.kr';
const STAGE_API_URL = 'https://web.api.ddocdoc.kr';
const PRODUCTION_API_URL = 'https://web.api.ddocdoc.kr';

export interface WEB_API
  extends SHORT_URL_API_V1,
    HOSPITAL_PATIENT_NOTICES_API_V1,
    RESERVATIONS_API_V1,
    SMS_CERTIFICATION_API_V1,
    USER_AGREEMENT_API_V1,
    WAITINGS_API_V1,
    HOSPITAL_RECEPTION_SYMPTOMS_API_V1,
    HOSPITAL_API_V1,
    SEARCH_API_V1,
    TREATMENTS_API_V1,
    USERS_API_V1,
    AUTH_API_V1,
    RECEPTION_PENALTIES_API_V1,
    MEMBERSHIPS_API_V1,
    NAME_CHECK_API_V1,
    EXTERNAL_API_V1,
    HOSPITAL_REVIEW_API_V1,
    CMS_API_V1 {}

const API_URL_MODEL = {
  test: DEVELOPMENT_API_URL,
  stage: STAGE_API_URL,
  prod: PRODUCTION_API_URL,
};

/**
 * Request Schema Validator
 * @description 요청 스키마를 검증합니다. 패턴이 필요한 경우 추가합니다.
 */
const ReqSchemaValidator = z
  .object({
    /**
     * hospitalId 프로퍼티가 존재하는 경우, 24자리의 16진수 문자열인지 검증합니다.
     */
    hospitalId: z.string().regex(/^[0-9a-fA-F]{24}$/, {
      message: '올바르지 않은 병원 아이디입니다.',
    }),
  })
  .partial();

/**
 * 요청 스키마 검증 함수
 */
function validateRequestSchema<T>(req: T) {
  for (const key in req) {
    try {
      const value = req[key];
      ReqSchemaValidator.parse(value);
    } catch (error: any) {
      const zodError = error as ZodError;
      const errorMessages = zodError.issues.reduce<string[]>((acc, issue) => {
        acc.push(`[${issue.code}] ${issue.message}`);
        return acc;
      }, []);
      throw new Error(errorMessages.join(', ') || '올바르지 않은 요청입니다.');
    }
  }
}

/**
 * Path param 이 포함된 API URL 문자열을 실제 값으로 치환
 */
function resolveApiUrlWithPathParams<T extends keyof WEB_API>(
  key: string,
  req: WEB_API[T]['req']
): string {
  if (req && 'params' in req) {
    return key.replace(/:(\w+)/g, (originalValue, key) => {
      const result = Object.entries(req.params as Record<string, string>);
      const matchedValue = result.find(([paramKey]) => paramKey === key)?.[1];
      return matchedValue ?? originalValue;
    });
  }
  return key;
}

const apiClient: AxiosInstance | null = null;

const createWebApi =
  <T extends keyof WEB_API>(
    key: T,
    config?: ApiConfig,
    cachedReq?: WEB_API[T]['req'],
    token?: string
  ) =>
  async (req: WEB_API[T]['req']) => {
    const reqWithCached: WEB_API[T]['req'] = {
      ...cachedReq,
      ...req,
    };

    validateRequestSchema(reqWithCached);

    const replacedApiUrlByPathParams = resolveApiUrlWithPathParams(
      key,
      reqWithCached
    );

    const [method = 'GET', apiUrl] = replacedApiUrlByPathParams.split(' ') as [
      ApiConfig['method'],
      string,
    ];

    const userToken = (() => {
      try {
        return (
          token ??
          localStorage?.getItem('userToken') ??
          sessionStorage?.getItem('userToken') ??
          undefined
        );
      } catch {
        return undefined;
      }
    })();

    const userId = (() => {
      try {
        return localStorage?.getItem('userId') ?? undefined;
      } catch {
        return undefined;
      }
    })();

    const viewerHeaders = await getViewerForwardHeaders();

    const apiConfig: ApiConfig = {
      method,
      baseURL: API_URL_MODEL[process.env.BUILD_ENV],
      ...config,
      headers: {
        'Content-Type': 'application/json',
        'dd-platform': 'web',
        ...viewerHeaders,
        ...(userId && { 'dd-user-id': userId }),
        ...(userToken && { Authorization: `Bearer ${userToken}` }),
        ...config?.headers,
      },
    };

    if (apiConfig.headers?.['Content-Type'] === 'multipart/form-data') {
      delete apiConfig.headers?.['Content-Type'];
    }

    const queryParams = (() => {
      if (reqWithCached && 'query' in reqWithCached && reqWithCached.query) {
        const searchParams = new URLSearchParams();
        Object.entries(reqWithCached.query).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
        return `?${searchParams.toString()}`;
      }
      return '';
    })();

    const body = (() => {
      if (
        !['GET', 'DELETE'].includes(method) &&
        reqWithCached &&
        'payload' in reqWithCached
      ) {
        if (reqWithCached.payload instanceof FormData) {
          return reqWithCached.payload;
        }
        return JSON.stringify(reqWithCached.payload);
      }
      return undefined;
    })();

    // SQL 인젝션 패턴을 포함한 문자열을 필터링
    const isValidURL = (url: string) => {
      const decodedUrl = decodeURIComponent(url);
      const sqlInjectionPattern =
        /\b(select|insert|drop|union|exec|declare|script|sleep|XOR|sysdate)\b/i;

      return !sqlInjectionPattern.test(decodedUrl);
    };

    if (!isValidURL(`${apiUrl}${queryParams}`)) {
      throw new Error(`잠재적인 SQL 인젝션 시도가 감지되었습니다.`);
    }

    const response = await fetch(
      `${apiConfig.baseURL}${apiUrl}${queryParams}`,
      {
        method,
        headers: {
          ...apiConfig.headers,
        },
        body,
        cache: apiConfig.cache ?? 'no-store',
      }
    );

    const data = await (async () => {
      if (response.status === 204) {
        return undefined;
      }

      switch (method) {
        case 'HEAD':
          return response;
        case 'GET':
          return response.json();
        default: {
          const responseJsonString = await response.text();
          return responseJsonString === ''
            ? undefined
            : JSON.parse(responseJsonString);
        }
      }
    })();

    return new Promise<WEB_API[T]['res']>(async (resolve, reject) => {
      if (response.ok) {
        resolve(data);
      } else {
        reject(data as ErrorRes);
      }
    });
  };

export { createWebApi };
